'use client';

import { useState, useEffect, useMemo } from 'react';
import { ChevronLeft, ChevronRight, ExternalLink, Mail } from 'lucide-react';
import { useTranslations, useLocale } from 'next-intl';
import { cn } from '@/lib/utils';
import { finlandToday, isUnavailable, type UnavailableRange } from '@/lib/availability-dates';
import { hasManualCalendar } from '@/lib/manual-cabins';
import { trackEvent } from '@/lib/analytics';

type CalendarState = {
  slug: string;
  status: 'loading' | 'ready' | 'unavailable';
  dates: UnavailableRange[];
  updatedAt?: string;
  source?: 'airbnb_public';
  coverageStart?: string;
  coverageEnd?: string;
};

interface AvailabilityCalendarProps {
  slug: string;
  propertyName: string;
  airbnbUrl: string;
  enquiryUrl?: string;
  hideBookingCta?: boolean;
  initialDate?: string;
  showPastBlocks?: boolean;
}

function getDaysInMonth(year: number, month: number) {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number) {
  // Monday = 0
  const day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1;
}

function formatMonth(year: number, month: number, locale: string) {
  return new Date(year, month).toLocaleDateString(locale, {
    month: 'long',
    year: 'numeric',
  });
}

function dateToStr(year: number, month: number, day: number): string {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

const WEEKDAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;

function MonthGrid({
  year,
  month,
  bookedRanges,
  weekdays,
  labels,
  locale,
  showPastBlocks,
  coverageStart,
  coverageEnd,
}: {
  year: number;
  month: number;
  bookedRanges: UnavailableRange[];
  labels: { available: string; unavailable: string; past: string; unknown: string };
  weekdays: string[];
  locale: string;
  showPastBlocks: boolean;
  coverageStart?: string;
  coverageEnd?: string;
}) {
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const today = finlandToday();

  const cells = [];

  // Empty cells before first day
  for (let i = 0; i < firstDay; i++) {
    cells.push(<div key={`empty-${i}`} />);
  }

  for (let day = 1; day <= daysInMonth; day++) {
    const dateStr = dateToStr(year, month, day);
    const isPast = dateStr < today;
    const unknown = Boolean((coverageStart && dateStr < coverageStart) || (coverageEnd && dateStr >= coverageEnd));
    const isBooked = isUnavailable(dateStr, bookedRanges);
    const showBlocked = !unknown && isBooked && (!isPast || showPastBlocks);
    const isToday = dateStr === today;

    cells.push(
      <time
        key={day}
        dateTime={dateStr}
        aria-label={`${new Date(`${dateStr}T12:00:00Z`).toLocaleDateString(locale, { timeZone: 'Europe/Helsinki', day: 'numeric', month: 'long', year: 'numeric' })}: ${unknown ? labels.unknown : showBlocked ? labels.unavailable : isPast ? labels.past : labels.available}`}
        data-availability={unknown ? 'unknown' : showBlocked ? 'unavailable' : isPast ? 'past' : 'available'}
        className={cn(
          'aspect-square flex items-center justify-center text-xs rounded-md relative',
          (unknown || (isPast && !showBlocked)) && 'text-gray-300',
          !unknown && !isPast && !isBooked && 'text-green-900 bg-green-100 border border-green-200',
          showBlocked && 'text-white bg-red-600 border border-red-700 font-semibold',
          isToday && 'ring-2 ring-[#9B7B3A] ring-offset-1'
        )}
      >
        {day}
      </time>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[248px]">
      <h3 className="text-center font-[family-name:var(--font-outfit)] text-sm font-light tracking-wider uppercase text-[#1A1A2E] mb-3">
        {formatMonth(year, month, locale)}
      </h3>
      <div className="grid grid-cols-7 gap-1">
        {weekdays.map((d) => (
          <div
            key={d}
            className="text-center text-[10px] uppercase tracking-wider text-[#8A8A9A] pb-1"
          >
            {d}
          </div>
        ))}
        {cells}
      </div>
    </div>
  );
}

export default function AvailabilityCalendar({
  slug,
  propertyName,
  airbnbUrl,
  enquiryUrl,
  hideBookingCta = false,
  initialDate,
  showPastBlocks = false,
}: AvailabilityCalendarProps) {
  const t = useTranslations('Availability');
  const manual = hasManualCalendar(slug);
  const bookingUrl = manual ? enquiryUrl : airbnbUrl;
  const locale = useLocale();
  const weekdays = WEEKDAY_KEYS.map((k) => t(`weekdays.${k}`));
  const [state, setState] = useState<CalendarState>({ slug: '', status: 'loading', dates: [] });
  const [retry, setRetry] = useState(0);
  const status = state.slug === slug ? state.status : 'loading';

  // The admin can focus a saved block, including a month outside the current view.
  const now = new Date(`${finlandToday()}T12:00:00`);
  const requestedStart = initialDate ? new Date(`${initialDate}T12:00:00`) : now;
  const initialMonth = Number.isFinite(requestedStart.getTime()) ? requestedStart : now;
  const [startMonth, setStartMonth] = useState(initialMonth.getMonth());
  const [startYear, setStartYear] = useState(initialMonth.getFullYear());

  useEffect(() => {
    let disposed = false;
    let request: AbortController | null = null;
    async function refresh() {
      if (request) return;
      const controller = new AbortController();
      request = controller;
      const timeout = window.setTimeout(() => controller.abort(), 15_000);
      try {
        const response = await fetch(`/api/availability/${encodeURIComponent(slug)}`, { cache: 'no-store', signal: controller.signal });
        const data = response.ok ? await response.json() : null;
        if (disposed) return;
        const validCoverage = data?.source !== 'airbnb_public' || (
          typeof data.coverageStart === 'string' && /^\d{4}-\d{2}-01$/.test(data.coverageStart)
          && typeof data.coverageEnd === 'string' && /^\d{4}-\d{2}-01$/.test(data.coverageEnd)
          && data.coverageEnd > data.coverageStart
        );
        const valid = data?.status === 'ready' && Array.isArray(data.dates)
          && data.dates.every((range: UnavailableRange) => /^\d{4}-\d{2}-\d{2}$/.test(range.checkIn) && /^\d{4}-\d{2}-\d{2}$/.test(range.checkOut) && range.checkOut > range.checkIn)
          && typeof data.updatedAt === 'string' && Number.isFinite(Date.parse(data.updatedAt)) && validCoverage;
        setState(valid
          ? { slug, status: 'ready', dates: data.dates, updatedAt: data.updatedAt, source: data.source, coverageStart: data.coverageStart, coverageEnd: data.coverageEnd }
          : { slug, status: 'unavailable', dates: [] });
      } catch {
        if (!disposed) setState({ slug, status: 'unavailable', dates: [] });
      } finally {
        window.clearTimeout(timeout);
        request = null;
      }
    }
    const visible = () => { if (document.visibilityState === 'visible') void refresh(); };
    void refresh();
    const timer = window.setInterval(visible, 5 * 60 * 1000);
    document.addEventListener('visibilitychange', visible);
    return () => {
      disposed = true;
      request?.abort();
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', visible);
    };
  }, [slug, retry]);

  const months = useMemo(() => {
    const result = [];
    for (let i = 0; i < 2; i++) {
      let m = startMonth + i;
      let y = startYear;
      if (m > 11) {
        m -= 12;
        y += 1;
      }
      result.push({ year: y, month: m });
    }
    return result;
  }, [startMonth, startYear]);

  function prevMonth() {
    if (startMonth === 0) {
      setStartMonth(11);
      setStartYear(startYear - 1);
    } else {
      setStartMonth(startMonth - 1);
    }
  }

  function nextMonth() {
    if (startMonth === 11) {
      setStartMonth(0);
      setStartYear(startYear + 1);
    } else {
      setStartMonth(startMonth + 1);
    }
  }

  // Visitors start at today; admins can inspect historical blocks too.
  const canGoPrev =
    (showPastBlocks || startYear > now.getFullYear() ||
    (startYear === now.getFullYear() && startMonth > now.getMonth())) &&
    (!state.coverageStart || dateToStr(startYear, startMonth, 1) > state.coverageStart);
  const nextWindowEnd = new Date(Date.UTC(startYear, startMonth + 3, 1)).toISOString().slice(0, 10);
  const canGoNext = !state.coverageEnd || nextWindowEnd <= state.coverageEnd;

  return (
    <div className="bg-white rounded-2xl border border-[#F0EDE5] shadow-sm overflow-hidden" role="region" aria-label={`${propertyName} ${t('title')}`}>
      <div className="px-4 sm:px-6 pt-5 pb-4">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
          <h2 className="font-[family-name:var(--font-outfit)] text-lg font-light tracking-wider text-[#1A1A2E]">
            {t('title')}
          </h2>
          <div className="flex items-center gap-2">
            <button
              onClick={prevMonth}
              disabled={!canGoPrev || status !== 'ready'}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md hover:bg-[#F0EDE5] transition-colors disabled:opacity-30"
              aria-label={t('prevMonth')}
            >
              <ChevronLeft size={16} className="text-[#5A5A6E]" />
            </button>
            <button
              onClick={nextMonth}
              disabled={!canGoNext || status !== 'ready'}
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md hover:bg-[#F0EDE5] transition-colors disabled:opacity-30"
              aria-label={t('nextMonth')}
            >
              <ChevronRight size={16} className="text-[#5A5A6E]" />
            </button>
          </div>
        </div>

        {status === 'loading' ? (
          <div className="h-64 flex items-center justify-center">
            <p className="text-sm text-[#8A8A9A]">{t('loading')}</p>
          </div>
        ) : status === 'unavailable' ? (
          <div className="rounded-xl bg-[#FAFAF5] px-5 py-10 text-center" role="status">
            <p className="text-sm text-[#5A5A6E]">{t(manual ? 'manualUnavailable' : 'unavailableMessage')}</p>
            <button type="button" onClick={() => setRetry(value => value + 1)} className="mt-3 text-sm underline underline-offset-4 text-[#9B7B3A]">{t('retry')}</button>
          </div>
        ) : (
          <div className="mx-auto mt-4 grid max-w-[520px] grid-cols-1 gap-6 sm:grid-cols-2">
            {months.map(({ year, month }) => (
              <MonthGrid
                key={`${year}-${month}`}
                year={year}
                month={month}
                bookedRanges={state.dates}
                labels={{ available: t('available'), unavailable: t(manual ? 'blocked' : 'booked'), past: t('past'), unknown: t('unknown') }}
                weekdays={weekdays}
                locale={locale}
                showPastBlocks={showPastBlocks}
                coverageStart={state.coverageStart}
                coverageEnd={state.coverageEnd}
              />
            ))}
          </div>
        )}

        {/* Legend */}
        {status === 'ready' && <>
        <div className="flex flex-wrap items-center gap-3 sm:gap-5 mt-5 pt-4 border-t border-[#F0EDE5]">
          <div className="flex items-center gap-2 text-xs text-[#5A5A6E]">
            <div className="w-4 h-4 rounded bg-green-100 border border-green-200" />
            {t('available')}
          </div>
          <div className="flex items-center gap-2 text-xs text-[#5A5A6E]">
            <div className="w-4 h-4 rounded bg-red-600 border border-red-700" />
            {t(manual ? 'blocked' : 'booked')}
          </div>
        </div>
        <p className="mt-3 text-xs leading-relaxed text-[#5A5A6E]">{t(manual ? 'manualNote' : state.source === 'airbnb_public' ? 'publicNote' : 'syncedNote')}</p>
        {state.updatedAt && <p className="mt-2 text-xs text-[#8A8A9A]">{t('updatedAt', { time: new Date(state.updatedAt).toLocaleString(locale, { timeZone: 'Europe/Helsinki', dateStyle: 'short', timeStyle: 'short' }) })}</p>}
        </>}
      </div>

      {/* Book CTA */}
      {!hideBookingCta && <div className="px-6 py-4 bg-[#F5F0E8] border-t border-[#E8E3D8]">
        <div className="flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-[#5A5A6E]">
            {t(manual ? 'manualBookingsVia' : 'bookingsVia', { name: propertyName })}
          </p>
          <a
            href={bookingUrl}
            target={manual ? undefined : "_blank"}
            rel="noopener noreferrer"
            onClick={() => {
              if (manual) return;
              trackEvent('book_airbnb_click', { property: propertyName });
              trackEvent('outbound_link_click', { url: airbnbUrl, property: propertyName });
            }}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#9B7B3A] text-white text-xs font-semibold uppercase tracking-wider hover:bg-[#C4A95E] transition-colors"
          >
            {t(manual ? 'enquireCta' : 'bookCta')}
            {manual ? <Mail size={12} /> : <ExternalLink size={12} />}
          </a>
        </div>
      </div>}
    </div>
  );
}
